import { useState } from 'react';
import { cn } from '@/utils/cn';
import { Badge } from '@/components/ui';
import { ComponentDetailRow } from './CompactPhaseScores';
import { getScoreVariant, normalizeScore } from './compactPhaseScoreHelpers';
import type { ComponentDetail, PhaseScore, RegionScore } from '@/types/api';

// ============================================================================
// Props
// ============================================================================

interface FormScoreBreakdownProps {
  /**
   * Phase scores containing component_details (BDS/JAS breakdown).
   * When present and non-empty, these take priority over legacy region_scores.
   */
  phaseScores?: PhaseScore[];
  /**
   * Legacy region scores (Procrustes-based). Shown only as a fallback when
   * no component_details are available on any phase.
   */
  legacyRegionScores?: RegionScore[];
  className?: string;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Collect and average component_details across all phases.
 *
 * Items with the same label+type are merged by averaging their avg_score and
 * trajectory_factor values. This gives a cross-phase view of each body part.
 */
function aggregateComponentDetails(phaseScores: PhaseScore[]): ComponentDetail[] {
  const accumulator = new Map<
    string,
    { label: string; type: 'bds' | 'jas'; scoreSum: number; trajectorySum: number; trajectoryCount: number; count: number }
  >();

  for (const phase of phaseScores) {
    if (!phase.component_details) continue;
    for (const detail of phase.component_details) {
      const key = `${detail.type}:${detail.label}`;
      const existing = accumulator.get(key);
      if (existing) {
        existing.scoreSum += detail.avg_score;
        existing.count += 1;
        if (detail.trajectory_factor !== null) {
          existing.trajectorySum += detail.trajectory_factor;
          existing.trajectoryCount += 1;
        }
      } else {
        accumulator.set(key, {
          label: detail.label,
          type: detail.type,
          scoreSum: detail.avg_score,
          count: 1,
          trajectorySum: detail.trajectory_factor !== null ? detail.trajectory_factor : 0,
          trajectoryCount: detail.trajectory_factor !== null ? 1 : 0,
        });
      }
    }
  }

  return Array.from(accumulator.values()).map((entry) => ({
    label: entry.label,
    type: entry.type,
    avg_score: entry.scoreSum / entry.count,
    trajectory_factor: entry.trajectoryCount > 0 ? entry.trajectorySum / entry.trajectoryCount : null,
  }));
}

// ============================================================================
// Main component
// ============================================================================

/**
 * Form Score Breakdown section.
 *
 * Shows per-bone (BDS) and per-joint (JAS) scores from component_details —
 * these are the ACTUAL values feeding the overall form score. Falls back to
 * the legacy Procrustes region_scores for old evaluations that pre-date the
 * BDS/JAS unified scoring architecture.
 */
function FormScoreBreakdown({ phaseScores, legacyRegionScores, className }: FormScoreBreakdownProps) {
  const [activeGroup, setActiveGroup] = useState<'bds' | 'jas' | null>(null);

  // Collect all component_details from phases
  const aggregated = phaseScores && phaseScores.length > 0
    ? aggregateComponentDetails(phaseScores)
    : [];

  const hasBdsJas = aggregated.length > 0;

  // Partition into BDS (bone direction) and JAS (joint angle)
  const bdsItems = aggregated.filter((d) => d.type === 'bds');
  const jasItems = aggregated.filter((d) => d.type === 'jas');

  // Compute group averages for summary badges
  const avgOf = (items: ComponentDetail[]) =>
    items.length === 0 ? null : items.reduce((s, d) => s + d.avg_score, 0) / items.length;

  const bdsAvg = avgOf(bdsItems);
  const jasAvg = avgOf(jasItems);

  // Which items to show — filter by active group tab, or show all
  const displayItems =
    activeGroup === 'bds' ? bdsItems :
    activeGroup === 'jas' ? jasItems :
    aggregated;

  // ---- New data path: BDS/JAS component_details ----
  if (hasBdsJas) {
    return (
      <div className={cn('space-y-4', className)}>

        {/* Group filter tabs */}
        {bdsItems.length > 0 && jasItems.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <GroupTab
              label="All"
              isActive={activeGroup === null}
              onClick={() => setActiveGroup(null)}
            />
            <GroupTab
              label="Bone Direction"
              description="How closely each bone segment aligns with the template"
              score={bdsAvg}
              count={bdsItems.length}
              isActive={activeGroup === 'bds'}
              onClick={() => setActiveGroup(activeGroup === 'bds' ? null : 'bds')}
            />
            <GroupTab
              label="Joint Angles"
              description="How closely joint bend angles match the template"
              score={jasAvg}
              count={jasItems.length}
              isActive={activeGroup === 'jas'}
              onClick={() => setActiveGroup(activeGroup === 'jas' ? null : 'jas')}
            />
          </div>
        )}

        {/* Component grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
          {displayItems.map((component, idx) => (
            <ComponentDetailRow key={`${component.type}:${component.label}:${idx}`} component={component} />
          ))}
        </div>

        {/* Caption */}
        <p className="text-xs text-text-muted">
          Scores reflect BDS/JAS averages across all evaluated phases.
          {' '}The trajectory dot shows how closely the movement PATH matched the template (not just the final pose).
        </p>
      </div>
    );
  }

  // ---- Fallback: legacy Procrustes region_scores ----
  if (legacyRegionScores && legacyRegionScores.length > 0) {
    return (
      <div className={cn('space-y-3', className)}>
        <p className="text-xs text-text-muted italic">
          This evaluation uses legacy region scoring (Procrustes-based). Re-evaluate to see the new BDS/JAS breakdown.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {legacyRegionScores.map((regionScore) => {
            const score = regionScore.score ?? 0;
            const percentage = score > 1 ? score : score * 100;
            const variant = getScoreVariant(normalizeScore(regionScore.score));
            const variantStyles = {
              success: 'bg-success/10 border-success/30',
              warning: 'bg-warning/10 border-warning/30',
              error: 'bg-danger/10 border-danger/30',
            };
            return (
              <div
                key={regionScore.region_id}
                className={cn(
                  'flex items-center justify-between p-3 rounded-lg border',
                  variantStyles[variant]
                )}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-text-primary truncate">
                    {regionScore.region_name}
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-text-muted">
                    <span>Avg Dev: {(regionScore.average_deviation ?? 0).toFixed(2)}</span>
                    <span>Errors: {regionScore.error_frames?.length ?? 0}</span>
                  </div>
                </div>
                <Badge variant={variant} size="lg">
                  {Math.round(percentage)}%
                </Badge>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return null;
}

// ============================================================================
// Sub-components
// ============================================================================

interface GroupTabProps {
  label: string;
  description?: string;
  score?: number | null;
  count?: number;
  isActive: boolean;
  onClick: () => void;
}

function GroupTab({ label, description, score, count, isActive, onClick }: GroupTabProps) {
  const variant = score !== null && score !== undefined ? getScoreVariant(score) : undefined;
  return (
    <button
      onClick={onClick}
      title={description}
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
        isActive
          ? 'bg-accent/20 border-accent text-accent'
          : 'bg-dark-elevated-1 border-border-subtle text-text-secondary hover:border-accent/50 hover:text-text-primary'
      )}
    >
      <span>{label}</span>
      {count !== undefined && (
        <span className="text-xs text-text-muted">({count})</span>
      )}
      {variant && score !== undefined && score !== null && (
        <Badge variant={variant} size="sm">
          {Math.round(score)}%
        </Badge>
      )}
    </button>
  );
}

export default FormScoreBreakdown;
